import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import {
  getReferralPacket,
  acceptReferral,
  declineReferral,
  requestMoreInfo,
} from "../APIs/referralApi";

const URGENCY_COLOR = {
  ROUTINE: "bg-gray-100 text-gray-600",
  WITHIN_7_DAYS: "bg-yellow-100 text-yellow-700",
  URGENT: "bg-orange-100 text-orange-700",
  EMERGENT: "bg-red-100 text-red-700",
};

export default function ReferralPacketModal({
  referralId,
  specialistProviderId,
  status,
  onClose,
  onActionDone,
}) {
  const [packet, setPacket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [declineReason, setDeclineReason] = useState("");
  const [infoNote, setInfoNote] = useState("");
  const [actionMode, setActionMode] = useState(null); // "decline" | "info" | null
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getReferralPacket(referralId)
      .then(setPacket)
      .catch(() => setError("Failed to load packet."))
      .finally(() => setLoading(false));
  }, [referralId]);

  const doAction = async (fn) => {
    setSubmitting(true);
    setError("");
    try {
      await fn();
      onActionDone?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Action failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-semibold">Referral Packet</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X size={20} />
          </button>
        </div>

        {loading && (
          <p className="p-6 text-sm text-gray-500 text-center">Loading...</p>
        )}
        {error && (
          <p className="p-6 text-sm text-red-600 text-center">{error}</p>
        )}

        {packet && (
          <div className="p-5 space-y-5">
            {/* Patient Info */}
            <Section title="Patient Info">
              <Row label="Name" value={packet.patientName} />
              <Row label="DOB" value={packet.patientDob} />
              <Row label="Insurance" value={packet.insurancePlan} />
              <Row label="Location" value={packet.patientLocation} />
            </Section>

            {/* Referring Provider */}
            <Section title="Referring Provider">
              <Row label="Name" value={packet.referringProviderName} />
              <Row label="Contact" value={packet.referringProviderContact} />
              <Row label="Facility" value={packet.facility} />
            </Section>

            {/* Clinical Info */}
            <Section title="Clinical Info">
              <Row label="Specialty" value={packet.specialtyName} />
              <div className="flex gap-2">
                <span className="text-xs text-gray-500 w-24 shrink-0">
                  Urgency:
                </span>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full font-medium ${URGENCY_COLOR[packet.urgency] ?? "bg-gray-100 text-gray-600"}`}
                >
                  {packet.urgency}
                </span>
              </div>
              <Row label="Reason" value={packet.reason} />
            </Section>

            {/* Meta */}
            <Section title="Referral Meta">
              <Row label="Routing" value={packet.routingMode} />
              <Row
                label="Created"
                value={
                  packet.createdAt
                    ? new Date(packet.createdAt).toLocaleDateString()
                    : "—"
                }
              />
              <Row label="Referral ID" value={packet.referralId} />
            </Section>

            {/* Action inputs */}
            {actionMode === "decline" && (
              <div>
                <label className="block text-sm font-medium mb-1">
                  Reason for declining
                </label>
                <textarea
                  className="w-full border rounded px-3 py-2 text-sm"
                  rows={3}
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  placeholder="Optional reason..."
                />
              </div>
            )}
            {actionMode === "info" && (
              <div>
                <label className="block text-sm font-medium mb-1">
                  Note / question for referring provider
                </label>
                <textarea
                  className="w-full border rounded px-3 py-2 text-sm"
                  rows={3}
                  value={infoNote}
                  onChange={(e) => setInfoNote(e.target.value)}
                  placeholder="What info do you need?"
                />
              </div>
            )}
            {error && <p className="text-red-600 text-sm">{error}</p>}
          </div>
        )}

        {/* Footer actions */}
        {packet && (
          <div className="flex flex-wrap gap-2 px-5 py-4 border-t bg-gray-50 justify-end">
            {actionMode === null && status === "PENDING_ACCEPTANCE" && (
              <>
                <button
                  onClick={() =>
                    doAction(() =>
                      acceptReferral(referralId, specialistProviderId),
                    )
                  }
                  disabled={submitting}
                  className="px-4 py-2 text-sm rounded bg-green-600 text-white disabled:opacity-40"
                >
                  Accept
                </button>
                <button
                  onClick={() => setActionMode("decline")}
                  className="px-4 py-2 text-sm rounded bg-red-100 text-red-700 hover:bg-red-200"
                >
                  Decline
                </button>
                <button
                  onClick={() => setActionMode("info")}
                  className="px-4 py-2 text-sm rounded bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
                >
                  Request More Info
                </button>
              </>
            )}
            {actionMode === "decline" && (
              <>
                <button
                  onClick={() => setActionMode(null)}
                  className="px-4 py-2 text-sm rounded border"
                >
                  Back
                </button>
                <button
                  disabled={submitting}
                  onClick={() =>
                    doAction(() =>
                      declineReferral(
                        referralId,
                        specialistProviderId,
                        declineReason,
                      ),
                    )
                  }
                  className="px-4 py-2 text-sm rounded bg-red-600 text-white disabled:opacity-40"
                >
                  {submitting ? "Declining..." : "Confirm Decline"}
                </button>
              </>
            )}
            {actionMode === "info" && (
              <>
                <button
                  onClick={() => setActionMode(null)}
                  className="px-4 py-2 text-sm rounded border"
                >
                  Back
                </button>
                <button
                  disabled={submitting || !infoNote.trim()}
                  onClick={() =>
                    doAction(() => requestMoreInfo(referralId, infoNote))
                  }
                  className="px-4 py-2 text-sm rounded bg-yellow-500 text-white disabled:opacity-40"
                >
                  {submitting ? "Sending..." : "Send Request"}
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm rounded border"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
        {title}
      </h3>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex gap-2 text-sm">
      <span className="text-gray-500 w-24 shrink-0">{label}:</span>
      <span className="text-gray-800">{value || "—"}</span>
    </div>
  );
}
